import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  errorMessage,
  updateCampaign,
  type CampaignDetail,
  type CampaignSettings,
  type EmailSender,
} from "@/api/email";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SenderField } from "@/components/email/SenderField";
import { SendingSettingsFields, settingsProblem } from "@/components/email/SendingSettingsFields";

export function CampaignSettingsDialog({
  open,
  onOpenChange,
  campaign,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: CampaignDetail;
  onSaved: (campaign: CampaignDetail) => void;
}) {
  const [name, setName] = useState(campaign.name);
  const [settings, setSettings] = useState<CampaignSettings>(campaign.settings);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(campaign.name);
    setSettings(campaign.settings);
    setError(null);
  }, [open, campaign]);

  const setSender = useCallback(
    (sender: EmailSender | null) => setSettings((s) => ({ ...s, sender })),
    [],
  );

  const problem = !name.trim() ? "Name the campaign." : settingsProblem(settings);
  // A different mailbox for contacts who already received an email from this campaign.
  const senderChanged =
    campaign.sequence.active_sequence &&
    !!campaign.settings.sender &&
    settings.sender?.user_id !== campaign.settings.sender.user_id;

  async function save() {
    if (problem) return;
    setSaving(true);
    try {
      const updated = await updateCampaign(campaign.project_slug, campaign.id, { name: name.trim(), settings });
      toast.success("Settings saved");
      onSaved(updated);
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err, "The settings could not be saved."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[92vh] max-w-xl flex-col gap-0 overflow-hidden p-0 outline-none"
        onOpenAutoFocus={(event) => {
          // Focus the dialog, not the name with its text selected.
          event.preventDefault();
          (event.currentTarget as HTMLElement).focus();
        }}
      >
        <DialogHeader className="border-b border-slate-100 px-6 py-4">
          <DialogTitle>Campaign settings</DialogTitle>
          <DialogDescription>
            {campaign.provider_mode === "NONE"
              ? "Applied to Lemlist at the first sync."
              : "Sent to Lemlist at the next sync (name, sender, days and hours)."}
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
          <div className="space-y-1.5">
            <Label htmlFor="settings-name" className="text-xs font-medium text-slate-600">
              Name
            </Label>
            <Input id="settings-name" value={name} maxLength={255} onChange={(e) => setName(e.target.value)} className="h-10" />
          </div>
          <div className="space-y-4 rounded-xl border border-slate-100 bg-slate-50/60 p-4">
            <p className="type-zonehead">Sending</p>
            <div className="space-y-1.5">
              <SenderField slug={campaign.project_slug} value={settings.sender} onChange={setSender} />
              {senderChanged && (
                <p className="text-xs leading-relaxed text-amber-700">
                  Contacts are already in this campaign: their next emails may leave from the new mailbox, outside the
                  thread of their first one.
                </p>
              )}
            </div>
            <SendingSettingsFields value={settings} onChange={setSettings} />
          </div>
          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>
        <DialogFooter className="gap-2 border-t border-slate-100 px-6 py-3.5">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!!problem || saving} title={problem ?? undefined} onClick={() => void save()}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
